import { useState } from 'react';
import { getRoomDataFromSlug } from '../config/roomMapping';
import { getSupabaseClient } from '../config/supabase';

export default function SupabaseTest() {
  const [slug, setSlug] = useState('');
  const [status, setStatus] = useState('Aguardando slug...');
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSearch = async () => {
    if (!slug.trim()) {
      setError('Por favor, informe um slug');
      return;
    }

    setLoading(true);
    setStatus('Buscando...');
    setError(null);
    setResult(null);

    try {
      const roomData = await getRoomDataFromSlug(slug.trim());

      if (roomData) {
        setStatus('✅ Dados encontrados!');
        setResult(roomData);
      } else {
        setStatus('⚠️ Nenhum dado encontrado');
        setError('Nenhuma sala encontrada com este slug');
      }
    } catch (err) {
      setStatus('❌ Erro na busca');
      setError(err.message || err.toString());
      console.error('❌ Erro ao buscar dados:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async () => {
    setLoading(true);
    setStatus('Testando conexão...');
    setError(null);
    setResult(null);

    try {
      const supabase = await getSupabaseClient();
      if (!supabase) {
        throw new Error('Cliente Supabase não disponível. Verifique as variáveis de ambiente.');
      }

      // Teste simples: buscar todas as views disponíveis
      const { data, error: testError } = await supabase
        .from('room_members_with_users')
        .select('slug, daily_room_url, user_name, user_email')
        .limit(5);

      if (testError) {
        throw testError;
      }

      setStatus('✅ Conexão OK!');
      setResult({
        connection: 'success',
        sample_data: data,
        message: `Conexão funcionando. ${data?.length || 0} registros encontrados na view.`,
      });
    } catch (err) {
      setStatus('❌ Erro na conexão');
      setError(err.message || err.toString());
      console.error('❌ Erro ao testar conexão:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      padding: '20px',
      fontFamily: 'monospace',
      maxWidth: '900px',
      margin: '0 auto'
    }}>
      <h1 style={{ marginBottom: '20px' }}>🧪 Teste de Conexão Supabase</h1>

      {/* Input para slug */}
      <div style={{
        padding: '15px',
        backgroundColor: '#f5f5f5',
        borderRadius: '8px',
        marginBottom: '20px'
      }}>
        <h3 style={{ marginTop: 0 }}>Buscar por Slug</h3>
        <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
          <input
            type="text"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            placeholder="Digite o slug (ex: nkm-uir-vtt)"
            style={{
              flex: 1,
              padding: '8px',
              fontSize: '14px',
              border: '1px solid #ccc',
              borderRadius: '4px'
            }}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          />
          <button
            type="button"
            onClick={handleSearch}
            disabled={loading}
            style={{
              padding: '8px 16px',
              fontSize: '14px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1
            }}
          >
            {loading ? 'Buscando...' : 'Buscar'}
          </button>
        </div>
        <button
          type="button"
          onClick={handleTestConnection}
          disabled={loading}
          style={{
            padding: '8px 16px',
            fontSize: '14px',
            backgroundColor: '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.6 : 1
          }}
        >
          Testar Conexão
        </button>
      </div>

      <div style={{
        padding: '15px',
        backgroundColor: '#f5f5f5',
        borderRadius: '8px',
        marginBottom: '20px'
      }}>
        <h2>Status: {status}</h2>

        {error && (
          <div style={{
            padding: '10px',
            backgroundColor: '#fee',
            border: '1px solid #fcc',
            borderRadius: '4px',
            marginTop: '10px',
            color: '#c00'
          }}>
            <strong>Erro:</strong> {error}
          </div>
        )}

        {result && (
          <div style={{ marginTop: '20px' }}>
            <div style={{
              padding: '15px',
              backgroundColor: '#efe',
              border: '1px solid #cfc',
              borderRadius: '4px',
              marginBottom: '15px'
            }}>
              <h3 style={{ marginTop: 0 }}>✅ Resultado da Busca</h3>

              {result.connection === 'success' ? (
                <div>
                  <p><strong>{result.message}</strong></p>
                  {result.sample_data && result.sample_data.length > 0 && (
                    <div>
                      <strong>Dados de exemplo da view:</strong>
                      <pre style={{
                        marginTop: '10px',
                        padding: '10px',
                        backgroundColor: '#fff',
                        borderRadius: '4px',
                        overflow: 'auto',
                        maxHeight: '300px',
                        fontSize: '12px'
                      }}>
                        {JSON.stringify(result.sample_data, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <div style={{ marginBottom: '15px' }}>
                    <strong>Slug:</strong> <code>{result.slug}</code>
                  </div>
                  <div style={{ marginBottom: '15px' }}>
                    <strong>Daily Room URL:</strong> <code style={{ fontSize: '12px' }}>{result.daily_room_url}</code>
                  </div>
                  <div style={{ marginBottom: '15px' }}>
                    <strong>Session ID:</strong> <code>{result.session_id}</code>
                  </div>
                  <div style={{ marginBottom: '15px' }}>
                    <strong>Participantes ({result.participants?.length || 0}):</strong>
                    {result.participants && result.participants.length > 0 && (
                      <ul style={{ marginTop: '10px', paddingLeft: '20px' }}>
                        {result.participants.map((p, idx) => (
                          <li key={idx} style={{ marginBottom: '8px' }}>
                            <div>
                              <strong>Nome:</strong> {p.user_name || 'N/A'}
                            </div>
                            <div>
                              <strong>Email:</strong> {p.user_email || 'N/A'}
                            </div>
                            <div>
                              <strong>User ID:</strong> <code style={{ fontSize: '11px' }}>{p.user_id}</code>
                            </div>
                            <div>
                              <strong>Session ID:</strong> <code style={{ fontSize: '11px' }}>{p.session_id}</code>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div>
                    <strong>Dados completos (JSON):</strong>
                    <pre style={{
                      marginTop: '10px',
                      padding: '10px',
                      backgroundColor: '#fff',
                      borderRadius: '4px',
                      overflow: 'auto',
                      maxHeight: '400px',
                      fontSize: '12px'
                    }}>
                      {JSON.stringify(result, null, 2)}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div style={{
        padding: '15px',
        backgroundColor: '#e7f3ff',
        border: '1px solid #b3d9ff',
        borderRadius: '8px',
        fontSize: '14px'
      }}>
        <strong>💡 Informações:</strong>
        <ul style={{ marginTop: '10px', paddingLeft: '20px' }}>
          <li>Digite um slug (ex: <code>nkm-uir-vtt</code>) e clique em &quot;Buscar&quot; para testar a view <code>room_members_with_users</code></li>
          <li>O resultado mostrará a URL da sala Daily e os dados dos participantes (nome, email, etc.)</li>
          <li>Use &quot;Testar Conexão&quot; para verificar se a view está acessível e ver dados de exemplo</li>
          <li>Se houver erro, verifique o console do navegador para mais detalhes</li>
        </ul>
      </div>
    </div>
  );
}

